import { type ApolloError, gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { ONBOARDING_FAMILY_FRAGMENT } from './useOnboardingState';

import type {
  GetOnboardingDependantsQuery,
  GetOnboardingDependantsQueryVariables,
} from './__generated__/useOnboardingDependants.gql';
import type { CaregiverRelationshipKind } from '../../__generated__/globalTypes';

const GET_ONBOARDING_DEPENDANTS = gql`
  query GetOnboardingDependants {
    getCurrentSession {
      id
      user {
        id
        family {
          ...OnboardingFamilyFragment
        }
      }
    }
  }
  ${ONBOARDING_FAMILY_FRAGMENT}
`;

export interface IOnboardingDependant {
  id: string;
  /** `CaregiverRelationship` row id — required for `updateCaregiverRelationship`. */
  caregiverRelationshipId: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  dateOfBirth: string | null;
  email: string | null;
  phoneNumber: string | null;
  relationship: CaregiverRelationshipKind;
  isStep: boolean;
  isInLaw: boolean;
  /** Whether this dependant lives with the acting caregiver. `null` until the living-situation step is answered. */
  livesWithCaregiver: boolean | null;
}

/** The partnership row joining the user's two dependants, when one exists. */
export interface IOnboardingPartnership {
  id: string;
  /** Whether the two partnered dependants live together. `null` until answered. */
  liveTogether: boolean | null;
}

export interface IOnboardingDependantsSnapshot {
  dependants: ReadonlyArray<IOnboardingDependant>;
  /** True when a partnership row already joins the two ids. False if either id is missing or no row matches. */
  arePartnered: (idA: string, idB: string) => boolean;
}

export interface IUseOnboardingDependantsResult {
  /**
   * This user's dependants (own caregiver-edge rows only), in API order.
   * Stabilized via `useMemo` so downstream `useEffect([dependants])`
   * consumers don't fire on every render.
   */
  dependants: ReadonlyArray<IOnboardingDependant>;
  /** True when a partnership row already links the user's two dependants. */
  partnershipExists: boolean;
  /** The partnership row joining the user's two dependants, or `null` when there isn't one. Carries the row id + `liveTogether` for the living-situation step to hydrate and update. */
  partnership: IOnboardingPartnership | null;
  loading: boolean;
  error: ApolloError | undefined;
  /**
   * Refetches the query and resolves with a snapshot of the latest server
   * state — both the dependants list and a partnership lookup. The review
   * step uses this after Phase 0 / Phase 1 mutations to decide whether to
   * link, since the reactive `partnershipExists` value above is captured at
   * `useCallback` definition time and goes stale during a submit.
   */
  refetch: () => Promise<IOnboardingDependantsSnapshot>;
}

/**
 * Reads the acting user's dependants for every loved-ones onboarding step
 * (count / entry / review + the contact entry/review pair). Filters
 * caregiver relationships to the user (skips rows owned by a co-caregiver)
 * and projects the relationship metadata onto each dependant alongside the
 * identity + contact columns.
 *
 * Pairs with `useCreateDependantAndRelationship` + `useLinkDependants` +
 * `useUpdateDependant` + `useUpdateDependantContact`. The create + link
 * mutations return the parent `Family` via `OnboardingFamilyFragment`,
 * writing the new `caregiverRelationships` / `dependantPartnerships` lists
 * into the normalized cache. The update mutations write back to the
 * existing `Dependant:<id>` record. Either way Apollo re-broadcasts this
 * query's result without a manual refetch.
 */
const projectDependants = (data: GetOnboardingDependantsQuery | undefined): ReadonlyArray<IOnboardingDependant> => {
  const user = data?.getCurrentSession?.user ?? null;
  if (!user) {
    return [];
  }
  return user.family.caregiverRelationships
    .filter((r) => r.caregiver.id === user.id)
    .map<IOnboardingDependant>((r) => ({
      id: r.dependant.id,
      caregiverRelationshipId: r.id,
      firstName: r.dependant.firstName,
      lastName: r.dependant.lastName,
      preferredName: r.dependant.preferredName ?? null,
      dateOfBirth: r.dependant.dateOfBirth ? String(r.dependant.dateOfBirth) : null,
      email: r.dependant.email ?? null,
      phoneNumber: r.dependant.phoneNumber ?? null,
      relationship: r.relationship,
      isStep: r.isStep,
      isInLaw: r.isInLaw,
      livesWithCaregiver: r.livesWithCaregiver ?? null,
    }));
};

const projectPartnership = (
  data: GetOnboardingDependantsQuery | undefined,
  dependants: ReadonlyArray<IOnboardingDependant>
): IOnboardingPartnership | null => {
  if (dependants.length !== 2) {
    return null;
  }
  const partnerships = data?.getCurrentSession?.user?.family.dependantPartnerships ?? [];
  const match = partnerships.find((p) => {
    const ids = new Set(p.dependants.map((d) => d.id));
    return ids.has(dependants[0].id) && ids.has(dependants[1].id);
  });
  return match ? { id: match.id, liveTogether: match.liveTogether ?? null } : null;
};

const projectSnapshot = (data: GetOnboardingDependantsQuery | undefined): IOnboardingDependantsSnapshot => {
  const partnerships = data?.getCurrentSession?.user?.family.dependantPartnerships ?? [];
  const partneredPairs = partnerships.map((p) => new Set(p.dependants.map((d) => d.id)));
  return {
    dependants: projectDependants(data),
    arePartnered: (idA, idB) => partneredPairs.some((pair) => pair.has(idA) && pair.has(idB)),
  };
};

export const useOnboardingDependants = (): IUseOnboardingDependantsResult => {
  const { data, loading, error, refetch } = useQuery<
    GetOnboardingDependantsQuery,
    GetOnboardingDependantsQueryVariables
  >(GET_ONBOARDING_DEPENDANTS, { fetchPolicy: 'cache-and-network' });

  const user = data?.getCurrentSession?.user ?? null;

  const dependants = React.useMemo<ReadonlyArray<IOnboardingDependant>>(() => projectDependants(data), [data]);

  const partnershipExists = React.useMemo(() => {
    if (!user || dependants.length !== 2) {
      return false;
    }
    return user.family.dependantPartnerships.some((p) => {
      const ids = new Set(p.dependants.map((d) => d.id));
      return ids.has(dependants[0].id) && ids.has(dependants[1].id);
    });
  }, [user, dependants]);

  const partnership = React.useMemo(() => projectPartnership(data, dependants), [data, dependants]);

  const refetchAndProject = React.useCallback(async () => {
    const result = await refetch();
    return projectSnapshot(result.data);
  }, [refetch]);

  return { dependants, partnershipExists, partnership, loading, error, refetch: refetchAndProject };
};
